import React, { useEffect, useRef, useState, useCallback } from 'react';
import {bindActionCreators} from 'redux';
import { connect } from 'react-redux';
import * as Actions from '../../redux/actions'; //Import your actions
import 'materialize-css';
import '../../styles/report.scss';
import M from 'materialize-css';

import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { orderByPosition } from '../orderByPosition';

const emptyFieldState = { isSaving: false, isSaved: false };

const PreSubmit = (props) => {

  const [fieldStates, setFieldStates] = useState({
    date_of_visit: { ...emptyFieldState },
    onsite_start: { ...emptyFieldState },
    onsite_end: { ...emptyFieldState }
  });

  const reportRef = useRef(props.report);
  const authTokenRef = useRef(props.authToken);
  const updateReportRef = useRef(props.updateReport);

  useEffect(() => {
    reportRef.current = props.report;
    authTokenRef.current = props.authToken;
    updateReportRef.current = props.updateReport;
  });

  const handleSaveConfirmation = useCallback((fieldsArray) => {
    setFieldStates((prev) => {
      const newState = { ...prev };
      fieldsArray.forEach((field) => {
        newState[field] = { isSaving: false, isSaved: true };
      });
      return newState;
    });

    setTimeout(() => {
      setFieldStates((prev) => {
        const newState = { ...prev };
        fieldsArray.forEach((field) => {
          newState[field] = { ...emptyFieldState };
        });
        return newState;
      });
    }, 2000);
  }, []);

  const handleSaveConfirmationRef = useRef(handleSaveConfirmation);
  useEffect(() => {
    handleSaveConfirmationRef.current = handleSaveConfirmation;
  }, [handleSaveConfirmation]);

  // Init pickers once per report open — not on every report field change,
  // otherwise destroy/re-init mid-save breaks the saving flash.
  useEffect(() => {
    if (!props.report) return;

    const reportId = props.report.id;
    const datepickers = document.querySelectorAll('.datepicker-presubmit');
    const timepickers = document.querySelectorAll('.timepicker-presubmit');

    M.Datepicker.init(datepickers, {
      autoClose: true,
      format: 'mmm d, yyyy',
      defaultDate: new Date(props.report.date_of_visit),
      setDefaultDate: true,
      container: document.querySelectorAll('.report-body'),
      maxDate: new Date(),
      onSelect: (d) => {
        setFieldStates((prev) => ({
          ...prev,
          date_of_visit: { isSaving: true, isSaved: false }
        }));
        updateReportRef.current({
          id: reportId,
          date_of_visit: d.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          })
        }, { fridge: true, freezer: authTokenRef.current }, () => {
          handleSaveConfirmationRef.current(['date_of_visit']);
        });
      }
    });

    M.Timepicker.init(timepickers, {
      autoClose: true,
      container: '.report-body',
      defaultTime: 'now',
      onCloseEnd: () => {
        const startVal = (timepickers[0]?.value || '').replace(/^0+/, '');
        const endVal = (timepickers[1]?.value || '').replace(/^0+/, '');
        const report = reportRef.current;
        const changed = [];

        if (startVal !== (report.onsite_start || '')) changed.push('onsite_start');
        if (endVal !== (report.onsite_end || '')) changed.push('onsite_end');
        if (!changed.length) return;

        setFieldStates((prev) => {
          const next = { ...prev };
          changed.forEach((field) => {
            next[field] = { isSaving: true, isSaved: false };
          });
          return next;
        });

        updateReportRef.current({
          id: reportId,
          onsite_start: startVal,
          onsite_end: endVal
        }, { fridge: true, freezer: authTokenRef.current }, () => {
          handleSaveConfirmationRef.current(changed);
        });
      }
    });

    return () => {
      datepickers.forEach((el) => M.Datepicker.getInstance(el)?.destroy());
      timepickers.forEach((el) => M.Timepicker.getInstance(el)?.destroy());
    };
  }, [props.report?.id]);

  const pullTests = (comp) => {
    if (!comp.tests) {return []};
    return Object.keys(comp.tests).map(id => comp.tests[id]).filter(t => t.type === 'test' && !t.deleted);
  }

  const outOfRange = (test) => {
    return test.result && (
      (test.max && test.result > test.max) || 
      (test.min && test.result < test.min)
    );
  }

  const recipient = (email) => {
    props.updateReport({
      id: props.report.id,
      recipients: [...props.report.recipients].filter(r => r !== email)
    }, {fridge: true, freezer: props.authToken}, null)
  }

  const countComponentNotes = (comp) => {
    if (!comp.notes) {return 0}
    // since notes can exist under components AND under tests, we count them all
    let compNotes = Object.values(comp.notes).filter(n => !n.deleted).length;
    let testNotes = Object.values(comp.tests || {}).filter(t => !t.deleted && t.note?.length).length;

    return compNotes + testNotes;
  }

  const hasEditedNodes = (comp) => {
    const completedTests = pullTests(comp).filter(t => t.result).length;
    return completedTests > 0 || countComponentNotes(comp) > 0;
  }

  const componentStats = (comp) => {
    const tests = pullTests(comp);
    const completed = tests.filter(t => t.result).length;
    const alerts = tests.filter(t => outOfRange(t)).length;
    const notes = countComponentNotes(comp);
    return { completed, alerts, notes };
  }

  const operatorReport = props.report.operator_report;

  const submitButtonText = () => {
    if (!props.report.recipients?.length > 0) {
      return "Please select at least one recipient"
    } else if (!operatorReport && !props.report.onsite_end) {
      return "Please select an on site end time"
    } else {
      return readyText
    }
  }

  const readyText = props.report.submitted ? "Resend Report" : "Submit Report";

  const isGeneralComponent = (c) => `${c.id}` === '0' || c.id === 'general';
  const isServiceLog = (c) => `${c.id}` === '-1';

  let compsObj = props.report.components;
  const componentsArray = orderByPosition(
    Object.keys(compsObj)
      .map(id => compsObj[id])
      .filter(c =>
        !props.report.hidden_components?.includes(`${c.id}`) &&
        !isServiceLog(c) &&
        hasEditedNodes(c)
      )
  );

  const systemCount = componentsArray.filter((c) => !isGeneralComponent(c)).length;

  const totals = componentsArray.reduce((acc, comp) => {
    const stats = componentStats(comp);
    acc.tests += stats.completed;
    acc.notes += stats.notes;
    acc.alerts += stats.alerts;
    return acc;
  }, { tests: 0, notes: 0, alerts: 0 });

  const renderPickerField = (fieldKey, input) => (
    <div className="picker-field">
      {input}
      <div className={`saved-indicator ${fieldStates[fieldKey].isSaved ? 'shown' : ''}`}>
        <CheckIcon />
      </div>
      {fieldStates[fieldKey].isSaving && (
        <div className="saving-flash shown" />
      )}
    </div>
  );

  return (
    <div className="report-modal" onClick={() => props.close()}>
      <div className="presubmit-container" onClick={(e) => e.stopPropagation()}>
        <header className="presubmit-header">
          <h2 className="presubmit-title">Review & send</h2>
          <p className="presubmit-meta">
            {componentsArray.length === 0
              ? 'Looks empty in here...'
              : [
                  systemCount > 0 ? `${systemCount} ${systemCount === 1 ? 'system' : 'systems'}` : null,
                  `${totals.tests} ${totals.tests === 1 ? 'test' : 'tests'}`,
                  totals.notes > 0 ? `${totals.notes} ${totals.notes === 1 ? 'note' : 'notes'}` : null,
                  totals.alerts > 0 ? `${totals.alerts} out of range` : null,
                ].filter(Boolean).join(' · ')}
          </p>
        </header>

        <section className="presubmit-section onsite" aria-label={operatorReport ? 'Reading date' : 'On site visit'}>
          <div className="presubmit-onsite">
            <label className="presubmit-onsite-field date" htmlFor="onsite-date">
              <span className="presubmit-onsite-label">{operatorReport ? 'Reading date' : 'On Site'}</span>
              {renderPickerField('date_of_visit', (
                <input
                  id="onsite-date"
                  className="picker-input datepicker datepicker-presubmit"
                  value={props.report.date_of_visit}
                  readOnly
                />
              ))}
            </label>

            {!operatorReport && <div className="presubmit-onsite-times">
              <label className="presubmit-onsite-field time" htmlFor="onsite-start">
                <span className="presubmit-onsite-label">Start</span>
                {renderPickerField('onsite_start', (
                  <input
                    id="onsite-start"
                    className="picker-input timepicker timepicker-presubmit"
                    value={props.report.onsite_start}
                    readOnly
                  />
                ))}
              </label>
              <span className="presubmit-onsite-range" aria-hidden="true">–</span>
              <label className="presubmit-onsite-field time" htmlFor="onsite-end">
                <span className="presubmit-onsite-label">End</span>
                {renderPickerField('onsite_end', (
                  <input
                    id="onsite-end"
                    className={`picker-input timepicker timepicker-presubmit ${props.report.onsite_end ? '' : 'empty'}`}
                    value={props.report.onsite_end}
                    placeholder=""
                    readOnly
                  />
                ))}
              </label>
            </div>}
          </div>
        </section>

        {componentsArray.length > 0 && (
          <section className="presubmit-section" aria-label="Report summary">
            <h3 className="presubmit-section-label">Report Summary</h3>
            <ul className="presubmit-systems">
              {componentsArray.map((comp) => {
                const { completed, alerts, notes } = componentStats(comp);
                const detail = [
                  completed > 0 ? `${completed} ${completed === 1 ? 'test' : 'tests'}` : null,
                  notes > 0 ? `${notes} ${notes === 1 ? 'note' : 'notes'}` : null,
                ].filter(Boolean).join(', ');

                return (
                  <li className={`presubmit-system${alerts > 0 ? ' has-alert' : ''}`} key={comp.id}>
                    <span className="presubmit-system-name">{comp.name}</span>
                    <span className="presubmit-system-stats">
                      {detail}
                      {alerts > 0 && (
                        <span className="presubmit-system-alert">
                          <WarningAmberIcon />
                          {alerts}
                        </span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        <section className="presubmit-section recipients" aria-label="Recipients">
          <h3 className="presubmit-section-label">Recipients</h3>
          <ul className="presubmit-recipients">
            { props.report?.recipients?.length > 0  ? props.report.recipients.map((email) => (
              <li className="presubmit-recipient" key={email}>
                <span className="presubmit-recipient-email">{email}</span>
                <button
                  type="button"
                  className="presubmit-recipient-remove"
                  onClick={() => recipient(email)}
                  aria-label={`Remove ${email}`}
                >
                  <CloseIcon />
                </button>
              </li>
            )) : <li className="presubmit-recipients-empty">None selected</li>}
          </ul>
          <button
            type="button"
            className="presubmit-add-recipient"
            onClick={() => props.triggerRecipientSelect()}
          >
            <AddIcon />
            Add recipient
          </button>
        </section>
      </div>

      <button
        type="button"
        className="final-submit"
        disabled={submitButtonText() !== readyText}
        onClick={() => props.submitReport(props.report, props.authToken, () => props.onSuccess())}
      >
        {submitButtonText()}
      </button>
    </div>
  )
}

function mapStateToProps(state, props) {
  return {
    authToken: state.authToken,
    report: state.report,
    tests: state.testFields
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(Actions, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(PreSubmit);
